import { useEffect, useRef } from 'react';
import {
  faArrowDown,
  faRotate,
  faSwap,
  faTrashCan,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faStop } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';

import SectionHeader from '@/components/shared/section-header';
import { LoadingIndicator, MessageResponse } from '@/components/ui/ai-elements/message';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Paragraph } from '@/components/ui/typography';

export interface StreamingStateProps {
  title: string;
  streamedContent: string;
  isStreaming: boolean;
  error?: string | null;
  onReplace: () => void;
  onInsertBelow?: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  onRetry: () => void;
}

export function StreamingState({
  title,
  streamedContent,
  isStreaming,
  error,
  onReplace,
  onInsertBelow,
  onDiscard,
  onCancel,
  onRetry,
}: StreamingStateProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasContent = streamedContent.trim().length > 0;
  const showErrorActions = !isStreaming && !!error;
  const showSuccessActions = !isStreaming && !error && hasContent;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !streamedContent) return;

    el.scrollTop = el.scrollHeight;
  }, [streamedContent]);

  return (
    <div className="flex flex-col">
      <SectionHeader
        title={title}
        size="sm"
        className="border-b pb-2"
        actions={
          isStreaming && (
            <Button variant="outline" onClick={onCancel} size="sm">
              <Icon icon={faStop} className="text-destructive" /> Cancel
            </Button>
          )
        }
      />
      <div ref={containerRef} className="max-h-75 overflow-auto">
        <div className="py-4">
          {error ? (
            <Paragraph className="text-sm text-destructive">{error}</Paragraph>
          ) : hasContent ? (
            <MessageResponse
              isLoading={false}
              isAnimating={isStreaming}
              className="tiptap text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 px-4"
            >
              {streamedContent}
            </MessageResponse>
          ) : (
            <LoadingIndicator />
          )}
        </div>
      </div>

      {(showErrorActions || showSuccessActions) && (
        <div className="flex gap-2 border-t pt-4 items-center">
          {showErrorActions ? (
            <>
              <Button variant="outline" size="sm" onClick={onRetry}>
                <Icon icon={faRotate} /> Retry
              </Button>
              <Button variant="ghost" size="sm" onClick={onDiscard} colorScheme="destructive">
                <Icon icon={faTrashCan} /> Discard
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onReplace} className="flex-1">
                <Icon icon={faSwap} />
                Replace
              </Button>
              {onInsertBelow && (
                <Button variant="outline" size="sm" onClick={onInsertBelow} className="flex-1">
                  <Icon icon={faArrowDown} />
                  Insert below
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onRetry} className="flex-1">
                <Icon icon={faRotate} />
                Retry
              </Button>
              <Button
                size="sm"
                onClick={onDiscard}
                colorScheme="destructive"
                variant="outline"
                className="flex-1"
              >
                <Icon icon={faTrashCan} />
                Discard
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
